import React from 'react';
import { ListenButton } from '../common/AudioGuide';

export const PORTFOLIO_EVM_GUIDE_ID = 'portfolio-evm-help';
const ACCENT = '#0f766e';

function Card({ icon, title, children }) {
  return (
    <div style={{
      background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.85rem 0.9rem',
      display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, background: '#f0fdfa', color: ACCENT,
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 4, fontSize: '0.95rem' }}>{title}</div>
        <div style={{ color: '#475569', fontSize: '0.86rem', lineHeight: 1.5 }}>{children}</div>
      </div>
    </div>
  );
}

export function PortfolioEvmHelpBody() {
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <p style={{ margin: 0, color: '#475569', fontSize: '0.9rem', lineHeight: 1.55 }}>
        The job EVM page answers “how is this job doing?”. This page answers the same question for a group of jobs:
        a program, a portfolio, a division, or any mix you pick. Nothing is re-estimated here — every number is the
        job’s own EVM, added up.
      </p>

      <Card icon="🎯" title="Pick the scope, press Apply">
        Four pickers at the top. <b>Portfolios</b> and <b>Programs</b> are the ones saved on the Corporation Resource
        Histogram (a program is a set of jobs; a portfolio is a set of programs). <b>Divisions</b> brings in every live job
        a company owns. <b>Jobs</b> lets you add single jobs. Tick any combination; the same job arriving twice is counted
        once. Press <b>Apply</b>. The page remembers your last scope for your login.
        <br />
        <b>Include unawarded estimates</b> adds estimates that have a P6 proposal but no job yet. They bring budget and
        nothing spent, so leave it off unless you want to see committed work.
      </Card>

      <Card icon="➕" title="What is added, what is recomputed">
        BAC, PV, EV, AC, and remaining hours are summed across the jobs. <b>EAC</b> is the sum of each job’s own EAC, so
        a job running at CPI 0.6 keeps its own overrun instead of being priced at the group average (hover the EAC card
        for the BAC ÷ blended CPI figure). <b>SPI</b>, <b>CPI</b>, and <b>TCPI</b> are recomputed from the sums — never an
        average of the job indices. The data date is the latest job’s P6 data date; if the jobs differ, a note says so
        and the table shows each job’s own date.
      </Card>

      <Card icon="📊" title="The chart">
        Bars are weekly hours stacked by job — choose Budget, Earned, Spent, or Remaining in the <b>Bars</b> dropdown.
        Lines are cumulative hours for the whole scope: budget, earned, spent, and the dashed forecast (earned so far plus
        remaining). The dotted vertical line is the closed week — spent and earned stop there, remaining starts the week
        after. Click a job in the legend to hide it from the bars.
      </Card>

      <Card icon="🧾" title="The job table">
        One row per job with its own BAC, PV, EV, AC, EAC, SPI, CPI, and % complete, so a bad group index can be traced
        to the job that caused it. <b>Came in via</b> says which program, portfolio, or division added the job. Notes
        flag things worth opening the job for: earned above budget, no spent yet, an index out of range, a data date
        more than four weeks behind the others. Flags never change the sums. Jobs with no P6 budget, earned, or Maestro
        spent are listed greyed and not counted; a job whose P6 read failed is listed as unavailable and left out.
      </Card>

      <Card icon="🔄" title="Refresh">
        <b>Apply</b> uses each job’s cached P6 read when it has one. <b>Refresh</b> re-reads P6 for every job in the
        scope — the first read of a job takes a few seconds, so a large division can take a minute.
      </Card>

      <Card icon="👤" title="Who sees it">
        Same people as the Corporation Resource Histogram: Resources, Admin, Construction, Estimating, Operations, and
        Accounting departments, plus managers and above.
      </Card>
    </div>
  );
}

export default function PortfolioEvmHelpModal({ onClose }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}
    >
      <div
        style={{ width: 'min(820px, 100%)', maxHeight: '88vh', background: 'white', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ background: ACCENT, color: 'white', padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Portfolio EVM — how to use this page</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ListenButton compact guideId={PORTFOLIO_EVM_GUIDE_ID} title="Portfolio EVM Help" accent={ACCENT} dark />
            <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ padding: '1rem 1.15rem 1.25rem', overflowY: 'auto', background: '#f8fafc' }}>
          <PortfolioEvmHelpBody />
        </div>
      </div>
    </div>
  );
}
